import React from 'react';
import PropTypes from 'prop-types';

/**
 * HamburgerToCloseIcon
 *
 * An animated icon that transforms between hamburger (2 horizontal lines)
 * and close (X) states based on the isOpen prop.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the icon should show as close (X) or hamburger
 * @param {string} [props.boxSize] - Size of the icon box (default: '20px')
 * @param {string} [props.color] - Color of the icon (default: 'currentColor')
 * @returns {React.ReactElement}
 */
export const HamburgerToCloseIcon = ({isOpen = false, boxSize = '20px', color = 'currentColor', ...props}) => {
  // Animation duration and easing (matching reference: 0.4s)
  const animationDuration = '0.4s';
  const animationEasing = 'ease';

  // Hamburger line positions (matching original hamburger.svg)
  const lineY1 = 5.25; // Top line (bar1 equivalent)
  const lineY2 = 13.25; // Bottom line (bar3 equivalent)
  const centerX = 10; // Center of viewBox horizontally

  const translateX = '-1.5px'; // Horizontal adjustment to center the X
  const translateYTop = '4px'; // Vertical adjustment for top line
  const translateYBottom = '-4px'; // Vertical adjustment for bottom line

  return (
    <svg
      {...props}
      fill="none"
      height={boxSize}
      viewBox="0 0 20 20"
      width={boxSize}
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Top line (bar1) - rotates -45deg and translates to form top-left to bottom-right diagonal */}
      <g
        style={{
          transformOrigin: `${centerX}px ${lineY1}px`,
          transition: `transform ${animationDuration} ${animationEasing}`,
          transform: isOpen ?
            `rotate(-45deg) translate(${translateX}, ${translateYTop})` :
            'none',
        }}
      >
        <line
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeOpacity="0.9"
          strokeWidth="1.5"
          x1="0.75"
          x2="19.25"
          y1={lineY1}
          y2={lineY1}
        />
      </g>
      {/* Bottom line (bar3) - rotates 45deg and translates to form top-right to bottom-left diagonal */}
      <g
        style={{
          transformOrigin: `${centerX}px ${lineY2}px`,
          transition: `transform ${animationDuration} ${animationEasing}`,
          transform: isOpen ?
            `rotate(45deg) translate(${translateX}, ${translateYBottom})` :
            'none',
        }}
      >
        <line
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeOpacity="0.9"
          strokeWidth="1.5"
          x1="0.75"
          x2="19.25"
          y1={lineY2}
          y2={lineY2}
        />
      </g>
    </svg>
  );
};

HamburgerToCloseIcon.displayName = 'HamburgerToCloseIcon';
HamburgerToCloseIcon.propTypes = {
  isOpen: PropTypes.bool,
  boxSize: PropTypes.string,
  color: PropTypes.string,
};
